/** @jsxImportSource @emotion/react */
/**
 * ClaimStatusBanner Component
 * SESSION_331: Non-Existent Business Claim Portal Status
 *
 * Displays a distinctive status banner for users with pending or rejected
 * business claims. Designed with a refined, geometric aesthetic that
 * clearly communicates status while maintaining visual elegance.
 *
 * Features:
 * - Pending state: Animated progress indicator, clear messaging
 * - Rejected state: Clear reason display, support link
 * - Responsive design with subtle animations
 * - Accessible color contrast and focus states
 */

import React, { useMemo } from 'react';
import { Box, Group, Stack } from '@mantine/core';
import {
  IconClock,
  IconX,
  IconMail,
  IconLoader2
} from '@tabler/icons-react';
import type { ClaimStatusBannerProps, ClaimStatusBannerVariant } from './ClaimStatusBanner.types';
import {
  bannerContainerStyles,
  pendingBannerStyles,
  rejectedBannerStyles,
  contentWrapperStyles,
  geometricAccentStyles,
  geometricAccentSecondaryStyles,
  iconContainerStyles,
  pendingIconStyles,
  titleStyles,
  messageStyles,
  progressBarContainerStyles,
  progressBarShimmerStyles,
  supportLinkStyles,
  businessNameStyles,
  rejectionReasonBoxStyles,
  rejectionReasonLabelStyles,
  rejectionReasonTextStyles
} from './ClaimStatusBanner.styles';

/**
 * Determines banner variant from claim status
 */
function getVariantFromClaim(status: string): ClaimStatusBannerVariant {
  if (status === 'Rejected') {
    return 'rejected';
  }
  return 'pending';
}

/**
 * ClaimStatusBanner - Displays business claim status to users
 */
export const ClaimStatusBanner: React.FC<ClaimStatusBannerProps> = ({
  claim,
  variant: propVariant,
  supportEmail = 'support@mentionedme.com',
  onSupportClick
}): React.ReactElement => {
  // Determine variant from claim status if not provided
  const variant = useMemo((): ClaimStatusBannerVariant => {
    return propVariant ?? getVariantFromClaim(claim.status);
  }, [propVariant, claim.status]);

  // Get business name for display
  const businessName = useMemo((): string => {
    return claim.requestedBusinessName ?? claim.businessName ?? 'Your business';
  }, [claim.requestedBusinessName, claim.businessName]);

  // Handle support link click
  const handleSupportClick = (): void => {
    if (onSupportClick) {
      onSupportClick();
    } else {
      window.location.href = `mailto:${supportEmail}?subject=Business Claim Support&body=Claim ID: ${claim.claimId}`;
    }
  };

  // Render pending banner
  if (variant === 'pending') {
    return (
      <Box
        css={[bannerContainerStyles, pendingBannerStyles]}
        role="status"
        aria-live="polite"
      >
        {/* Geometric accent shapes */}
        <Box css={geometricAccentStyles} aria-hidden="true" />
        <Box css={geometricAccentSecondaryStyles} aria-hidden="true" />

        <Box css={contentWrapperStyles}>
          <Group gap="md" align="flex-start" wrap="nowrap">
            {/* Icon */}
            <Box css={[iconContainerStyles, pendingIconStyles]}>
              <IconLoader2 size={24} color="#ffffff" />
            </Box>

            {/* Content */}
            <Stack gap={8} style={{ flex: 1 }}>
              <h3 css={titleStyles}>Setting Up Your Business</h3>
              <p css={messageStyles}>
                We&apos;re reviewing your request for{' '}
                <span css={businessNameStyles}>{businessName}</span>.
                You&apos;ll receive an email notification once your business is
                approved and ready to start tracking mentions.
              </p>

              {/* Animated progress indicator */}
              <Box css={progressBarContainerStyles}>
                <Box css={progressBarShimmerStyles} />
              </Box>
            </Stack>
          </Group>
        </Box>
      </Box>
    );
  }

  // Render rejected banner
  return (
    <Box
      css={[bannerContainerStyles, rejectedBannerStyles]}
      role="alert"
      aria-live="assertive"
    >
      {/* Geometric accent shapes */}
      <Box css={geometricAccentStyles} aria-hidden="true" />
      <Box css={geometricAccentSecondaryStyles} aria-hidden="true" />

      <Box css={contentWrapperStyles}>
        <Group gap="md" align="flex-start" wrap="nowrap">
          {/* Icon */}
          <Box css={iconContainerStyles}>
            <IconX size={24} color="#ffffff" />
          </Box>

          {/* Content */}
          <Stack gap={8} style={{ flex: 1 }}>
            <h3 css={titleStyles}>Business Claim Not Approved</h3>
            <p css={messageStyles}>
              Unfortunately, your request for{' '}
              <span css={businessNameStyles}>{businessName}</span>{' '}
              could not be approved at this time.
            </p>

            {/* Rejection reason if provided */}
            {claim.rejectionReason && (
              <Box css={rejectionReasonBoxStyles}>
                <p css={rejectionReasonLabelStyles}>Reason</p>
                <p css={rejectionReasonTextStyles}>
                  &ldquo;{claim.rejectionReason}&rdquo;
                </p>
              </Box>
            )}

            {/* Support link */}
            <button
              css={supportLinkStyles}
              onClick={handleSupportClick}
              type="button"
              aria-label={`Contact support at ${supportEmail}`}
            >
              <IconMail size={16} />
              Contact Support
            </button>
          </Stack>
        </Group>
      </Box>
    </Box>
  );
};
